import * as React from "react";
import { Handle, NodeToolbar, Position, type NodeProps } from "@xyflow/react";
import { FileChartLine, Layers, Link2, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { workspaceNodeTitle } from "../workspaceGraphUtils";
import {
  WORKSPACE_INSERT_NODE_INNER_SIZE,
  WORKSPACE_INSERT_NODE_SIZE,
  type WorkspaceInsertNodeData,
} from "../workspacePlaceholderUtils";
import {
  useWorkspaceNodeToolbarSide,
  WORKSPACE_NODE_TOOLBAR_OFFSET_PX,
} from "../useWorkspaceNodeToolbarSide";

const EDGE_HANDLE_CLASS =
  "!pointer-events-none !h-px !w-px !min-h-0 !min-w-0 !border-0 !bg-transparent !opacity-0";

function reportLabel(reportNode: WorkspaceInsertNodeData["reportNodes"][number]): string {
  return workspaceNodeTitle(reportNode);
}

export function WorkspaceInsertNode({ data }: NodeProps): React.ReactElement {
  const flowData = data as WorkspaceInsertNodeData;
  const menuOpen = Boolean(flowData.showActionsMenu);
  const disabled = Boolean(flowData.mutating);
  const toolbarSide = useWorkspaceNodeToolbarSide(menuOpen);

  return (
    <>
      <div
        className="relative flex items-center justify-center"
        style={{ width: WORKSPACE_INSERT_NODE_SIZE, height: WORKSPACE_INSERT_NODE_SIZE }}
      >
        <Handle
          type="target"
          position={Position.Left}
          id="target"
          className={EDGE_HANDLE_CLASS}
          style={{ left: 0, top: "50%", transform: "translate(-50%, -50%)" }}
        />

        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          data-workspace-node=""
          data-workspace-node-variant="insert-card"
          title="Add node"
          aria-label="Add strategy or report from this account path"
          aria-disabled={disabled || undefined}
          aria-expanded={menuOpen}
          className={cn(
            "nodrag nopan flex rotate-45 items-center justify-center rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-shadow",
            "hover:border-primary/55",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            menuOpen && "border-primary/55 ring-2 ring-primary/45 ring-offset-1 ring-offset-background",
            disabled && "pointer-events-none opacity-50",
            "dark:border-input dark:bg-sidebar dark:text-sidebar-foreground"
          )}
          style={{
            width: WORKSPACE_INSERT_NODE_INNER_SIZE,
            height: WORKSPACE_INSERT_NODE_INNER_SIZE,
          }}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (disabled) return;
            flowData.onToggleMenu?.();
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              event.stopPropagation();
              flowData.onToggleMenu?.();
            }
          }}
        >
          <Plus className="size-4 -rotate-45" strokeWidth={2.25} aria-hidden />
        </div>

        <Handle
          type="source"
          position={Position.Right}
          id="source"
          className={EDGE_HANDLE_CLASS}
          style={{ right: 0, top: "50%", transform: "translate(50%, -50%)" }}
        />
      </div>

      <NodeToolbar
        isVisible={menuOpen}
        position={toolbarSide.position}
        align={toolbarSide.align}
        offset={WORKSPACE_NODE_TOOLBAR_OFFSET_PX}
        className="nodrag nopan nowheel"
      >
        <div
          data-workspace-node-actions=""
          data-workspace-placeholder-menu=""
          data-slot="dropdown-menu-content"
          role="menu"
          className="nodrag nopan nowheel z-[1200] min-w-56 overflow-visible rounded-[6px] bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="px-2 py-1.5" data-workspace-node-actions-header="">
            <div className="flex flex-col gap-0.5">
              <span
                data-workspace-node-actions-type=""
                className="font-[family-name:var(--font-primary)] text-[10px] uppercase tracking-wide text-[color:var(--brand-tropical-surf)]"
              >
                Insert
              </span>
                <span
                  data-workspace-node-actions-title=""
                  className="whitespace-nowrap font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
                >
                  Add node
                </span>
            </div>
          </div>
          <div className="-mx-1 my-1 h-px bg-border" />
          {flowData.reportNodes.map((reportNode) => (
            <React.Fragment key={reportNode.id}>
              <button
                type="button"
                role="menuitem"
                data-slot="dropdown-menu-item"
                disabled={disabled || !flowData.onAddStrategy}
                className={cn(
                  "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                  "hover:bg-accent hover:text-accent-foreground",
                  "disabled:pointer-events-none disabled:opacity-50"
                )}
                onClick={() => {
                  flowData.onAddStrategy?.(flowData.accountNode, reportNode, "create");
                }}
              >
                <Layers className="size-4 shrink-0" />
                <span className="whitespace-nowrap">
                  {flowData.reportNodes.length === 1
                    ? "Create strategy"
                    : `Create strategy · ${reportLabel(reportNode)}`}
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                data-slot="dropdown-menu-item"
                disabled={disabled || !flowData.onAddStrategy}
                className={cn(
                  "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                  "hover:bg-accent hover:text-accent-foreground",
                  "disabled:pointer-events-none disabled:opacity-50"
                )}
                onClick={() => {
                  flowData.onAddStrategy?.(flowData.accountNode, reportNode, "existing");
                }}
              >
                <Link2 className="size-4 shrink-0" />
                <span className="whitespace-nowrap">
                  {flowData.reportNodes.length === 1
                    ? "Add existing strategy"
                    : `Add existing strategy · ${reportLabel(reportNode)}`}
                </span>
              </button>
            </React.Fragment>
          ))}
          <button
            type="button"
            role="menuitem"
            data-slot="dropdown-menu-item"
            disabled={disabled || !flowData.onAddReport}
            className={cn(
              "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
              "hover:bg-accent hover:text-accent-foreground",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
            onClick={() => {
              flowData.onAddReport?.(flowData.accountNode, "create");
            }}
          >
            <FileChartLine className="size-4 shrink-0" />
            <span className="whitespace-nowrap">Create report</span>
          </button>
          <button
            type="button"
            role="menuitem"
            data-slot="dropdown-menu-item"
            disabled={disabled || !flowData.onAddReport}
            className={cn(
              "relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
              "hover:bg-accent hover:text-accent-foreground",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
            onClick={() => {
              flowData.onAddReport?.(flowData.accountNode, "existing");
            }}
          >
            <Link2 className="size-4 shrink-0" />
            <span className="whitespace-nowrap">Add existing report</span>
          </button>
        </div>
      </NodeToolbar>
    </>
  );
}
